// https://stackoverflow.com/questions/36633033/how-to-organize-typescript-interfaces?

import type {
  AlchemyActionType,
  ConditionFieldEditorValue,
  Operator,
} from "replo-runtime/shared/enums";
import type { Context } from "replo-runtime/store/ReploVariable";
import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import type { Action } from "schemas/actions";
import type { Component } from "schemas/component";
import type { metafieldTypes, productStatus } from "schemas/element";
import type { BundleConfigDependency } from "schemas/generated/element";
import type { ConditionField, ReploState } from "schemas/generated/symbol";
import type { ProductRef } from "schemas/product";
import type {
  GradientStop,
  RuntimeStyleAttribute,
} from "schemas/styleAttribute";

import isObject from "lodash-es/isObject";

export type WidthOrHeight = "width" | "height";

export type ProductId = string | number;
export type VariantId = string | number;

export type SharedStatePayload<Value = any> = {
  key: string;
  value: Value | ((prevValue: Value) => Value);
};

export type SyncRuntimeStatePayload<Value = any> = {
  key: string;
  value: Value;
};

/**
 * Note (Mariano, 2022-06-01): This is meant to contain all the feature types available in our editor.
 */
export type FeatureType = "shopifyLiquid" | "shopifyAppBlocks";

interface ElementMetafieldCommon {
  shopifyId?: string;
  namespace: string;
  key: string;
}

interface IntegerMetafield extends ElementMetafieldCommon {
  type: "number_integer";
  value: number;
}

export interface StringMetafield extends ElementMetafieldCommon {
  type: "string";
  value: string;
}

interface BooleanMetafield extends ElementMetafieldCommon {
  type: "boolean";
  value: boolean;
}

export type ElementMetafield = ElementMetafieldCommon &
  (IntegerMetafield | StringMetafield | BooleanMetafield);

export interface ElementGenericMetafield {
  key: string;
  namespace: string;
  type: string;
  value: string | number | boolean;
  shopifyId?: string;
}

export type ElementMetafieldType = ElementMetafield["type"];

export interface Option {
  value: string;
  label: string;
}

export type PredefinedVariantType =
  | "hover"
  | "loading"
  | "outOfStock"
  | "activeTab"
  | "selectedOption"
  | "selectedVariant"
  | "selectedSellingPlan"
  | "collapsibleOpen"
  | "beforeAfterDragging"
  | "tooltipOpen"
  | "selectedListItem"
  | "firstItemIsActive"
  | "lastItemIsActive"
  | "currentItemIsActive";

export type PredefinedVariant = {
  name: string;
  query: VariantQuery;
  predefinedVariantType: PredefinedVariantType;
};

export interface ConditionStatement {
  id: string;
  field: ConditionField;
  operator?: Operator | null;
  value?: any;
}

export type VariantQuery = {
  type?: string;
  operator?: string;
  statements: ConditionStatement[];
};

export interface VariantWithState extends ReploState {
  isDefault: boolean;
  isActive: boolean;
}

export type ProductIdsWithVariantIds = Record<string, string[] | null>;

/**
 * Given an action type, retrieve its value. I.e.:
 * ActionValueOf<AlchemyActionType.GoToItem> would be number, since
 * the `value` of the to to item action is number
 */
type ActionValueOf<TargetType extends AlchemyActionType> = Extract<
  Action,
  { type: TargetType }
>["value"];

/**
 * Same as Action, except the `value` is optional.
 */
export type ActionWithNullableValue = {
  [K in Action["type"]]: { type: K; value: ActionValueOf<K> | undefined };
}[Action["type"]];

export interface AnimationField {
  id: string;
  type: string;
  name: string;
  placeholder?: string;
  options: {
    label: string;
    value: string;
    override?: { [key: string]: string };
  }[];
}

export interface BoxShadow {
  id: string;
  shadowType: "dropShadow" | "inset" | "none";
  offsetX: string;
  offsetY: string;
  blur: string;
  spread: string;
  shadowColor: string;
}

export type TextOutline = {
  width: string;
  color?: string;
};

export type RenderPreviewEnvironment = "componentPreview" | "editor";

// Note (Fran, 2023-03-09): We need this namespace because there are two
// versions of okendo widget and for each one we need to publish different
// liquid code. The window object is not accessible when we publish, so we
// need to pass this through as part of runtime context
export type OkendoNamespace = "okendoReviews" | "okeWidgetApi";

export interface RuntimeFeatureFlags {
  carouselDebug: boolean;
}

export interface RenderComponentAttributes {
  ref: any;
  key: string;
  id?: string;
  "data-rid": string;
  "data-alchemy-carousel-id"?: string;
  "data-alchemy-url-hashmark"?: string;
  "data-alchemy-url-hashmark-offset"?: number;
  "data-alchemy-url-hashmark-ignore"?: boolean;
  "data-replo-price"?: boolean;
  "data-replo-compare-price"?: boolean;
  "data-replo-compare-percentage"?: boolean;
  "data-replo-compare-difference"?: boolean;
  "data-replo-selling-plan"?: boolean;
  "data-product-id"?: number | string;
  "data-variant-id"?: number | string;
  tabIndex?: number;
  role?: string;
  className?: string;
  "aria-expanded"?: boolean;
  "aria-controls"?: string;
  "aria-hidden"?: boolean;
  "aria-checked"?: boolean;
  "aria-labelledby"?: string;
  "aria-selected"?: boolean;
  "aria-current"?: boolean;
  "aria-label"?: string;

  style?: React.CSSProperties;

  onClick?: (e: any) => void;
  onScroll?: (e: any) => void;
  onMouseEnter?: (e: any) => void;
  onKeyPress?: (e: any) => void;
  onKeyDown?: (e: any) => void;
}

export interface RenderComponentProps {
  component: Component;
  context: Context;
  componentAttributes: RenderComponentAttributes;
  extraAttributes?: Partial<RenderComponentAttributes>;
  overrideComponentTagType?: "li"; // Can add more supported override tag types here as needed
}

export type DynamicStyleDataMap = Record<
  string,
  { key: RuntimeStyleAttribute; value: unknown }
>;

export type ProductStatus = (typeof productStatus)[number];

export interface StoreVariant {
  id: number;
  sku: string | null;
  title: string;
  option1: string;
  option2?: string | null;
  option3?: string | null;
  available: boolean;
  name: string;
  price: string;
  compare_at_price: string | null;
  featured_image?: { src: string } | null;
  selling_plan_allocations?: {
    selling_plan_id: number;
    selling_plan_group_id: string;
  }[];
  selling_plan_ids?: number[];
}

interface StoreSellingPlanOptionDefinition {
  name: string;
  position: number;
  values: (string | null)[];
}

type StoreSellingPlanOption = {
  name?: string | undefined;
  position: number;
  value: string;
};

// https://shopify.dev/api/liquid/objects#selling_plan_price_adjustment
export interface StoreSellingPlanPriceAdjustment {
  value: number;
  value_type: "percentage" | "fixed_amount" | "price";
}

interface StoreSellingPlan {
  id: number;
  name: string;
  appName: string | null;
  description: string | null;
  options: StoreSellingPlanOption[];
  price_adjustments: StoreSellingPlanPriceAdjustment[];
}

export interface StoreSellingPlanGroup {
  app_id: string | null;
  options: StoreSellingPlanOptionDefinition[];
  selling_plans: StoreSellingPlan[];
}

export interface StoreProduct {
  title: string;
  images: string[];
  id: number | string;
  type: string;
  status: ProductStatus;
  variants: StoreVariant[];
  vendor?: string;
  options: string[];
  featured_image: string | null;
  description: string;
  handle: string;
  // Note (Noah, 2022-08-19, REPL-3674): Apparently, selling plan groups can be undefined
  // in liquid objects
  selling_plan_groups?: StoreSellingPlanGroup[];
}

export interface LiquidProduct {
  title: string;
  images: string[];
  id: number;
  variants: StoreVariant[];
  options: string[];
  featured_image: string | null;
  description: string;
  vendor?: string;
  handle: string;
  type: string;
  selling_plan_groups?: StoreSellingPlanGroup[];
  status: ProductStatus;
}

// NOTE (Matt, 2023-09-15): We need to reference this list of Price Labels in alchemy-component.ts.
export type VariantPriceLabels = keyof VariantPriceTypes;

interface VariantPriceTypes {
  rawPrice: string;
  price: string;
  priceRounded: string;
  priceWithoutSellingPlanDiscount: string;
  priceWithoutSellingPlanDiscountRounded: string;
  displayPrice: string;
  displayPriceWithoutQuantity: string;
  displayPriceWithoutSellingPlanDiscount: string;
  displayPriceRounded: string;
  compareAtPrice: string | null;
  compareAtPriceRounded: string | null;
  compareAtDisplayPrice: string | null;
  compareAtDisplayPriceRounded: string | null;
  compareAtPriceDifference: string | null;
  compareAtPriceDifferencePercentage: string | null;
  compareAtPriceDifferenceRounded: string | null;
  compareAtDisplayPriceDifference: string | null;
  compareAtDisplayPriceDifferenceRounded: string | null;
}

export interface ReploShopifyVariant extends VariantPriceTypes {
  id: number;
  // HACK (Noah, 2021-07-28): The variant needs to have variantId and productId
  // because of how the context resolution for dynamic data works - when there's
  // a variant, we treat it as a productRef and get the variant again, since it
  // calls into the same codepath that happens if it's a non-dynamic productRef.
  // There is probably a better way to do this
  variantId: number;
  productId: string | number;
  sku: string | null;
  name: string;
  option1: string;
  option2?: string | null;
  option3?: string | null;
  title: string;
  available: boolean;
  featuredImage: string | null;
  productHandle: string;
  variantMetafields?: MetafieldValuesMappingWithoutType;
  sellingPlanIds: number[];
}

export type ReploShopifyOptionKey = "option1" | "option2" | "option3";
export interface ReploShopifyOptionValue {
  title: string;
  available?: boolean;
}
export interface ReploShopifyOption {
  key: ReploShopifyOptionKey;
  name: string;
  values?: ReploShopifyOptionValue[];
}

export type SelectedOptionValuesMapping = Record<string, string | null>;

type ShopifySellingPlanPriceAdjustment = {
  value: number;
  value_type: "percentage" | "fixed_amount" | "price";
};

export interface ShopifySellingPlan {
  id: number | string;
  name: string;
  description: string | null;
  options: StoreSellingPlanOption[];
  priceAdjustments: ShopifySellingPlanPriceAdjustment[];
  appId?: string | null;
  appName?: string | null;
  price?: string;
}

export interface ShopifySellingPlanGroup {
  appId: string | null;
  options: StoreSellingPlanOptionDefinition[];
  sellingPlans: ShopifySellingPlan[];
}

export interface ReploShopifyProduct {
  id: number;
  // NOTE (Noah, 2021-07-24): It's weird to have productId here but currently
  // we need it because some places (like Product components inside Product List
  // components) assume that ProductRefs (productId, variantId) are passed down
  // in component context, but in actuality it's the full product that is passed
  // down. In the future hopefully we can think of some way around this, it's
  // pretty weird
  productId: string | number;
  title: string;
  type: string | undefined;
  images: string[];
  status: ProductStatus;
  vendor?: string;
  featured_image: string | null;
  variants: ReploShopifyVariant[];
  variant: ReploShopifyVariant;
  variantId: number;
  options: ReploShopifyOption[];
  /**
   * Names of the options.
   *
   * @deprecated (Noah, 2023-12-29, REPL-9856): Use `options` instead
   *
   * This unfortunately can't be renamed without a migration since it's used in
   * dynamic data. We should remove it in the future after updating any dynamic
   * data in elements on prod
   */
  optionsValues: string[];
  description: string;
  handle: string;
  quantity: number;
  productMetafields?: MetafieldValuesMappingWithoutType;
  sellingPlanGroups: ShopifySellingPlanGroup[];
}

export interface Edges {
  top: number | string;
  left: number | string;
  right: number | string;
  bottom: number | string;
}

export interface NumericBounds {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface BoundedComponent {
  bounds: NumericBounds;
  component: Component;
}

export interface Position {
  x: number;
  y: number;
}

export interface Range {
  lower: number;
  upper: number;
}

export interface ReploPriceRule {
  id: any;
  allocationMethod: "each" | "across";
  entitledProductIds?: (string | number)[];
  entitledVariantIds?: number[];
  allProductsAreEntitled: boolean;
  prerequisiteQuantityRange?: {
    greaterThanOrEqualTo: number;
  };
  prerequisiteSubtotalRange?: {
    greaterThanOrEqualTo: number;
  };
  prerequisiteToEntitlementPurchase?: {
    amount: number;
  };
  prerequisiteProductIds?: (string | number)[];
  prerequisiteVariantIds?: number[];
  targetSelection: "entitled" | "all";
  targetType: "lineItem" | "shippingLine";
  title: string;
  value: number;
  valueType: "fixedAmount" | "percentage";
}

export type MetafieldType = (typeof metafieldTypes)[number];

export type MetafieldEntityType = "variant" | "product";

type MetafieldOwnerType = "PRODUCT" | "PRODUCTVARIANT" | "PAGE";

export type MetafieldNamespace = string;
export type MetafieldKey = string;
export interface MetafieldDefinition {
  key: MetafieldKey;
  name: string;
  namespace: MetafieldNamespace;
  id: string;
  type: MetafieldType;
  ownerType: MetafieldOwnerType;
  description: string | null;
}

export type MetafieldValuesMapping = Record<
  MetafieldNamespace,
  Record<MetafieldKey, { type: MetafieldType; value: string | number }>
>;
export type MetafieldValuesMappingWithoutType = Record<
  MetafieldNamespace,
  Record<MetafieldKey, string | number>
>;
export type ProductMetafieldMapping = Record<ProductId, MetafieldValuesMapping>;
export type VariantMetafieldMapping = Record<
  StoreVariant["id"],
  MetafieldValuesMapping
>;

export interface MetafieldsMappings {
  productMetafieldsMapping: ProductMetafieldMapping;
  variantMetafieldsMapping: VariantMetafieldMapping;
}

export interface EditVariantConditionValueProps {
  type: ConditionFieldEditorValue;
  value: ConditionStatement["value"];
  onChange: (value: any) => void;
  placeholder: string | null;
}

export interface ShopifyBlog {
  id: string;
  handle: string;
  name: string;
  template_suffix?: string;
  tags?: string;
}

export type EditorPropType =
  | "index"
  | "visibility"
  | "collapsibility"
  | "interactions"
  | "activeBeforeAfterState";

/**
 * Types of data dependencies that a component may have
 */
export type DependencyType = "metafields" | "products";

/**
 * Generic interface for metafield dependency
 */
interface GenericMetafieldDependency {
  type: "metafields";
  productHandle: string;
  metafieldKeys: {
    key: string;
    namespace: string;
    type: string;
    value?: string | number | boolean;
  }[];
}

/**
 * A dependency on data from a product metafield
 */
export interface ProductMetafieldsDependency
  extends GenericMetafieldDependency {
  entityType: "product";
  productId: string;
}

/**
 * A dependency on data from a variant metafield
 */
export interface VariantMetafieldsDependency
  extends GenericMetafieldDependency {
  entityType: "variant";
  variantId: string;
}

/**
 * Dependency on a metafield (from unspecified type, different types may have
 * different fields)
 */
export type MetafieldsDependency =
  | ProductMetafieldsDependency
  | VariantMetafieldsDependency
  | never;

export interface ProductsDependency {
  type: "products";
  productIds: (string | number)[];
}

/**
 * A generalized dependency. Depending on type, may have different fields
 */
export type Dependency =
  | MetafieldsDependency
  | ProductsDependency
  | BundleConfigDependency
  | never;

export type Dependencies = Record<string, Dependency[]>;

export interface LinkData {
  url: string;
  isUnderlined: boolean;
  isNewTab: boolean;
}

// Note (Chance 2023-07-14) Gradients must have at least 1 stop
export type GradientStops = [GradientStop, ...GradientStop[]];

export interface Gradient {
  tilt: string;
  stops: GradientStop[];
}

export type SolidValue = { type: "solid"; color?: string | null };
export type GradientValue = { type: "gradient"; gradient: Gradient };
export type SolidOrGradient = SolidValue | GradientValue;

export const isSolidValue = (value: SolidOrGradient): value is SolidValue => {
  return value.type === "solid" && value.color !== null;
};

export const isGradient = (value: any): value is Gradient => {
  return (
    isObject(value) &&
    value.hasOwnProperty("tilt") &&
    value.hasOwnProperty("stops")
  );
};

export type GradientOrSolidOnChangeProps =
  | {
      allowsGradientSelection: false;
      onPreviewChange?(value: string | null): void;
      onChange(value: string | null): void;
      value: string | ReploMixedStyleValue | null;
    }
  | {
      allowsGradientSelection: true;
      onPreviewChange?(value: SolidOrGradient): void;
      onChange(value: SolidOrGradient): void;
      value: SolidOrGradient | ReploMixedStyleValue | null;
    };

export interface SwatchImage {
  src: string;
  id?: string;
}

export interface SwatchValue {
  color?: SolidOrGradient;
  image?: SwatchImage;
  imageList?: SwatchImage[];
}

// TODO (Gabe 2023-10-09): This is duplicated from
// apps/replo-publisher/src/types/swatch.ts, updates here need to be made to the
// schema as well.
export interface SwatchOptionRow {
  id: string;
  key: {
    // NOTE (Gabe 2023-10-09): productId is optional because it used to not
    // exist which made having multi product swatches difficult. In order to not
    // break existing swatches it is optional and only used to uniquely identify
    // swatch values when it is present.
    productId?: number | string;
    name: string;
    value: string;
  };
  label: string;
  value?: SwatchValue;
}

export interface SwatchVariantRow {
  id: string;
  key: {
    productId: string | number;
    variantId: string | number;
  };
  label: string;
  value?: SwatchValue | null;
}

export type SwatchType = "option" | "variant";

interface SwatchData {
  type: SwatchType;
  productIds?: (string | number)[];
  options?: SwatchOptionRow[];
  variants?: SwatchVariantRow[];
}

export interface Swatch {
  id: string;
  name: string;
  data: SwatchData;
}

export interface ShopifyComponentCommonProps {
  component?: Component;
  componentId: string;
  componentAttributes: RenderComponentAttributes;
  isLiquidSupported: boolean;
}

export interface BaseSharedLiquidProps {
  attributes: RenderComponentAttributes;
  component: Component;
  context: Context;
}

export interface SharedLiquidReviewsProps extends BaseSharedLiquidProps {
  liquidSource: string;
  reviewsComponentType: "Product Rating" | "Reviews";
}

export interface AddProductVariantToCartEditorPropsValue {
  product: ProductRef;
  quantity?: number | string;
  redirectToCart: boolean;
  redirectToCheckout?: boolean;
  sellingPlanId?: number | string | null;
  allowThirdPartySellingPlan?: boolean;
}
